import { useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { adsApi } from '@/api/client';
import { AutoTargetingFields } from '@/components/campaign-create/create-adgroup/AutoTargetingFields';
import { CampaignBidAdjustmentsPanel } from '@/components/campaign-create/create-adgroup/CampaignBidAdjustmentsPanel';
import { SpAdGroupSection } from '@/components/campaign-create/create-adgroup/SpAdGroupSection';
import { ManualTargetingKindFields } from '@/components/campaign-create/create-adgroup/ManualTargetingKindFields';
import { KeywordTargetingPanel } from '@/components/campaign-create/KeywordTargetingPanel';
import { NegativeKeywordTargetingSection } from '@/components/campaign-create/NegativeKeywordTargetingSection';
import { NegativeProductTargetingSection } from '@/components/campaign-create/NegativeProductTargetingSection';
import { ProductTargetingPanel } from '@/components/campaign-create/ProductTargetingPanel';
import { TypedProductList } from '@/components/campaign-create/create-adgroup/TypedProductList';
import {
  extractAdGroupMutationResults,
  formatCreateCampaignUpstreamError,
} from '@/utils/campaignCreate';
import { buildProductAdPayload, extractGenericMutationResults } from '@/utils/spCampaignCreateDraft';
import { parseSpTargetsPayload } from '@/api/spTargets';
import {
  buildNegativeTargetClauses,
  buildSpAutoTargetUpdates,
  buildSpKeywordPayloads,
  buildSpManualProductTargetPayloads,
  buildSpNegativeKeywordPayloads,
  campaignBiddingStrategyCopy,
  campaignBiddingStrategyFromRaw,
  defaultAutoTargetingGroups,
  defaultSpAdGroupName,
  validateSpAutoAdGroupForm,
  validateSpManualAdGroupForm,
  type SpAutoTargetingMode,
} from '@/utils/spAdGroupCreate';
import type {
  SpAddedKeyword,
  SpAddedNegativeBrand,
  SpAddedNegativeKeyword,
  SpAddedNegativeProduct,
  SpAddedProductTarget,
  SpAdvertisedProduct,
  SpManualTargetingKind,
} from '@/types/spCampaignCreate';

export function CreateSpAdGroupForm({
  profileId,
  campaignId,
  campaignRaw,
  targeting,
  onSuccess,
  onCancel,
}: {
  profileId: string;
  campaignId: string;
  campaignRaw?: Record<string, unknown>;
  targeting: 'AUTO' | 'MANUAL';
  onSuccess: () => void;
  onCancel: () => void;
}) {
  const qc = useQueryClient();
  const [name, setName] = useState(() => defaultSpAdGroupName());
  const [defaultBid, setDefaultBid] = useState('3');
  const [targetingMode, setTargetingMode] = useState<SpAutoTargetingMode>('default');
  const [groups, setGroups] = useState(() => defaultAutoTargetingGroups('3'));
  const [products, setProducts] = useState<SpAdvertisedProduct[]>([]);
  const [targetingKind, setTargetingKind] = useState<SpManualTargetingKind>('KEYWORD');
  const [keywords, setKeywords] = useState<SpAddedKeyword[]>([]);
  const [productTargets, setProductTargets] = useState<SpAddedProductTarget[]>([]);
  const [negativeKeywords, setNegativeKeywords] = useState<SpAddedNegativeKeyword[]>([]);
  const [negativeProducts, setNegativeProducts] = useState<SpAddedNegativeProduct[]>([]);
  const [negativeBrands, setNegativeBrands] = useState<SpAddedNegativeBrand[]>([]);
  const biddingStrategy = campaignBiddingStrategyFromRaw(campaignRaw) ?? 'LEGACY_FOR_SALES';
  const biddingStrategyCopy = campaignBiddingStrategyCopy(biddingStrategy);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const automatic = targeting === 'AUTO';

  const invalidate = async () => {
    let syncRes;
    try {
      syncRes = await adsApi.syncCampaignCatalog(profileId, campaignId, 'SP');
    } catch (err) {
      throw new Error(
        `Ad group was created on Amazon, but saving it to the database failed. ${formatCreateCampaignUpstreamError(err)}`
      );
    }
    if (!syncRes.success) {
      throw new Error(
        syncRes.message ||
          'Ad group was created on Amazon, but it was not saved to the database yet.'
      );
    }
    const synced = syncRes.data as { skipped?: boolean; ad_groups?: number };
    if (!synced.skipped && typeof synced.ad_groups === 'number' && synced.ad_groups < 1) {
      throw new Error(
        'Ad group was created on Amazon, but the database sync did not store any ad groups yet.'
      );
    }
    await qc.invalidateQueries({ queryKey: ['sp-ad-groups', profileId, campaignId] });
    await qc.invalidateQueries({ queryKey: ['sp-ad-groups', profileId] });
    await qc.invalidateQueries({ queryKey: ['sp-product-ads', profileId] });
    await qc.invalidateQueries({ queryKey: ['sp-keywords', profileId] });
    await qc.invalidateQueries({ queryKey: ['sp-targets', profileId] });
  };

  const failAfterPartial = async (message: string) => {
    try {
      await invalidate();
      setSubmitError(message);
    } catch (err) {
      setSubmitError(`${message} ${formatCreateCampaignUpstreamError(err)}`);
    }
  };

  const applyAutoTargetUpdates = async (adGroupId: string, defaultBidNum: number) => {
    // Amazon creates the four AUTO clauses with the ad group — only update bids/state.
    if (targetingMode !== 'custom') return true;

    const listRes = await adsApi.listSpTargets(profileId, {
      campaign_ids: [campaignId],
      ad_group_ids: [adGroupId],
      states: ['ENABLED', 'PAUSED'],
      max_results: 100,
    });
    if (!listRes.success) {
      await failAfterPartial(
        `Ad group created, but loading automatic targets failed: ${listRes.message || 'Unknown error'}`
      );
      return false;
    }
    const { targets } = parseSpTargetsPayload(listRes.data);
    const updates = buildSpAutoTargetUpdates({
      targets,
      targetingMode,
      defaultBid: defaultBidNum,
      groups,
    });
    if (updates.length === 0) {
      await failAfterPartial(
        'Ad group created, but Amazon did not return automatic targeting groups to update yet.'
      );
      return false;
    }

    for (const update of updates) {
      const updateRes = await adsApi.updateSpTarget(profileId, update);
      if (!updateRes.success) {
        await failAfterPartial(
          `Ad group created, but updating automatic targeting failed: ${
            updateRes.message || 'Unknown error'
          }`
        );
        return false;
      }
      const updateParsed = extractGenericMutationResults(updateRes.data, 'targetId');
      if (updateParsed.errors.length > 0) {
        await failAfterPartial(
          `Ad group created, but updating automatic targeting failed: ${updateParsed.errors.join(' ')}`
        );
        return false;
      }
    }
    return true;
  };

  const createTargeting = async (adGroupId: string, defaultBidNum: number) => {
    if (automatic) {
      return applyAutoTargetUpdates(adGroupId, defaultBidNum);
    }

    if (targetingKind === 'KEYWORD') {
      const keywordPayloads = buildSpKeywordPayloads({ campaignId, adGroupId, keywords });
      if (keywordPayloads.length > 0) {
        const kwRes = await adsApi.createSpKeywords(profileId, { keywords: keywordPayloads });
        if (!kwRes.success) {
          await failAfterPartial(`Ad group created, but keywords failed: ${kwRes.message || 'Unknown error'}`);
          return false;
        }
        const kwParsed = extractGenericMutationResults(kwRes.data, 'keywordId');
        if (kwParsed.errors.length > 0) {
          await failAfterPartial(`Ad group created, but keywords failed: ${kwParsed.errors.join(' ')}`);
          return false;
        }
      }
      return true;
    }

    const targetRes = await adsApi.createSpTargets(profileId, {
      targetingClauses: buildSpManualProductTargetPayloads({ campaignId, adGroupId, productTargets }),
    });
    if (!targetRes.success) {
      await failAfterPartial(
        `Ad group created, but product targets failed: ${targetRes.message || 'Unknown error'}`
      );
      return false;
    }
    const targetParsed = extractGenericMutationResults(targetRes.data, 'targetId');
    if (targetParsed.errors.length > 0) {
      await failAfterPartial(`Ad group created, but product targets failed: ${targetParsed.errors.join(' ')}`);
      return false;
    }
    return true;
  };

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitError(null);
    const error = automatic
      ? validateSpAutoAdGroupForm({ name, defaultBid, products, targetingMode, groups })
      : validateSpManualAdGroupForm({
          name,
          defaultBid,
          products,
          targetingKind,
          keywords,
          productTargets,
        });
    if (error) {
      setSubmitError(error);
      return;
    }
    const defaultBidNum = parseFloat(defaultBid);

    setSubmitting(true);
    try {
      const agRes = await adsApi.createSpAdGroups(profileId, {
        adGroups: [
          {
            campaignId,
            name: name.trim(),
            defaultBid: defaultBidNum,
            state: 'ENABLED',
          },
        ],
      });
      if (!agRes.success) {
        setSubmitError(agRes.message || 'Failed to create ad group');
        return;
      }
      const agParsed = extractAdGroupMutationResults(agRes.data);
      if (agParsed.errors.length > 0) {
        setSubmitError(agParsed.errors.join(' '));
        return;
      }
      const adGroupId = agParsed.successIds[0];
      if (!adGroupId) {
        setSubmitError('Ad group was created but no ad group ID was returned.');
        return;
      }

      const adsRes = await adsApi.createSpProductAds(profileId, {
        productAds: products.map((product) => buildProductAdPayload(campaignId, adGroupId, product)),
      });
      if (!adsRes.success) {
        await failAfterPartial(`Ad group created, but product ads failed: ${adsRes.message || 'Unknown error'}`);
        return;
      }
      const adsParsed = extractGenericMutationResults(adsRes.data, 'adId');
      if (adsParsed.errors.length > 0) {
        await failAfterPartial(`Ad group created, but product ads failed: ${adsParsed.errors.join(' ')}`);
        return;
      }

      const targetingOk = await createTargeting(adGroupId, defaultBidNum);
      if (!targetingOk) return;

      if (negativeKeywords.length > 0) {
        const negKwRes = await adsApi.createSpNegativeKeywords(profileId, {
          negative_keywords: buildSpNegativeKeywordPayloads({
            campaignId,
            adGroupId,
            keywords: negativeKeywords,
          }),
        });
        if (!negKwRes.success) {
          await failAfterPartial(
            `Ad group created, but negative keywords failed: ${negKwRes.message || 'Unknown error'}`
          );
          return;
        }
        const negParsed = extractGenericMutationResults(negKwRes.data, 'negative_keyword_id');
        if (negParsed.errors.length > 0) {
          await failAfterPartial(
            `Ad group created, but negative keywords failed: ${negParsed.errors.join(' ')}`
          );
          return;
        }
      }

      const negativeTargets = buildNegativeTargetClauses({
        campaignId,
        adGroupId,
        products: negativeProducts,
        brands: negativeBrands,
      });
      if (negativeTargets.length > 0) {
        const negProdRes = await adsApi.createSpNegativeTargets(profileId, {
          negativeTargetingClauses: negativeTargets,
        });
        if (!negProdRes.success) {
          await failAfterPartial(
            `Ad group created, but negative products failed: ${negProdRes.message || 'Unknown error'}`
          );
          return;
        }
      }

      await invalidate();
      onSuccess();
    } catch (err) {
      setSubmitError(formatCreateCampaignUpstreamError(err));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <p className="text-sm text-foreground-muted mb-4">
        {automatic
          ? 'Creates a Sponsored Products automatic ad group, advertised products, and targeting via Amazon Ads APIs.'
          : 'Creates a Sponsored Products manual ad group, advertised products, and keyword or product targeting via Amazon Ads APIs.'}
      </p>
      {submitError ? (
        <div className="mb-4 text-sm text-error bg-error-subtle border border-error/20 rounded-xl px-3 py-2">
          {submitError}
        </div>
      ) : null}
      <form onSubmit={submit} className="space-y-4">
        <SpAdGroupSection title="Ad group">
          <div>
            <label className="block text-sm font-medium text-foreground-muted mb-1">Ad group name</label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="w-full border border-border rounded-lg px-3 py-2"
              disabled={submitting}
              placeholder="Ad group - DD/MM/YYYY HH:MM:SS.mmm"
            />
          </div>
        </SpAdGroupSection>
        <SpAdGroupSection title="Products">
          <TypedProductList
            profileId={profileId}
            products={products}
            onChange={setProducts}
            disabled={submitting}
            framed={false}
          />
        </SpAdGroupSection>
        <SpAdGroupSection title="Campaign bidding strategy">
          <div className="space-y-1">
            <p className="text-sm font-medium text-foreground">{biddingStrategyCopy.label}</p>
            <p className="text-xs text-foreground-muted">{biddingStrategyCopy.description}</p>
            <p className="text-xs text-foreground-subtle">
              Taken from this campaign. It cannot be changed when creating an ad group.
            </p>
          </div>
        </SpAdGroupSection>
        <SpAdGroupSection title="Bid adjustments">
          <CampaignBidAdjustmentsPanel
            campaignRaw={campaignRaw}
            defaultBid={defaultBid}
            biddingStrategyLabel={biddingStrategyCopy.label}
          />
        </SpAdGroupSection>
        {automatic ? (
          <SpAdGroupSection title="Automatic targeting">
            <AutoTargetingFields
              defaultBid={defaultBid}
              onDefaultBidChange={setDefaultBid}
              targetingMode={targetingMode}
              onTargetingModeChange={setTargetingMode}
              groups={groups}
              onGroupsChange={setGroups}
              disabled={submitting}
              framed={false}
            />
          </SpAdGroupSection>
        ) : (
          <>
            <SpAdGroupSection title="Manual targeting">
              <ManualTargetingKindFields
                value={targetingKind}
                onChange={setTargetingKind}
                disabled={submitting}
              />
            </SpAdGroupSection>
            <div hidden={targetingKind !== 'KEYWORD'}>
              <SpAdGroupSection title="Keyword targeting">
                <KeywordTargetingPanel
                  profileId={profileId}
                  advertisedProducts={products}
                  biddingStrategy={biddingStrategy}
                  defaultBid={defaultBid}
                  added={keywords}
                  onChange={setKeywords}
                  disabled={submitting}
                />
              </SpAdGroupSection>
            </div>
            <div hidden={targetingKind !== 'PRODUCT'}>
              <SpAdGroupSection title="Product targeting">
                <ProductTargetingPanel
                  profileId={profileId}
                  advertisedProducts={products}
                  defaultBid={defaultBid}
                  added={productTargets}
                  onChange={setProductTargets}
                  disabled={submitting}
                />
              </SpAdGroupSection>
            </div>
          </>
        )}
        <NegativeKeywordTargetingSection
          negativeKeywords={negativeKeywords}
          onChange={setNegativeKeywords}
          disabled={submitting}
        />
        <NegativeProductTargetingSection
          negativeProducts={negativeProducts}
          onProductsChange={setNegativeProducts}
          negativeBrands={negativeBrands}
          onBrandsChange={setNegativeBrands}
          disabled={submitting}
        />
        <div className="flex items-center justify-end gap-2 pt-2">
          <button
            type="button"
            onClick={onCancel}
            className="text-sm px-3 py-1.5 rounded-lg border border-border text-foreground hover:bg-surface-muted"
            disabled={submitting}
          >
            Cancel
          </button>
          <button
            type="submit"
            className="text-sm px-3 py-1.5 bg-brand text-brand-foreground rounded-lg hover:bg-brand-hover font-medium disabled:opacity-50"
            disabled={submitting}
          >
            {submitting ? 'Creating…' : 'Create ad group'}
          </button>
        </div>
      </form>
    </>
  );
}
